import { AxiosResponse } from 'axios'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { TemplateActionsMenu } from '../TemplateActionsMenu/TemplateActionsMenu'
import { ArchiveTemplateModal } from '../ArchiveTemplateModal/ArchiveTemplateModal'
import { SetDefaultTemplateModal } from '../SetDefaultTemplateModal/SetDefaultTemplateModal'
import { SetDefaultTemplateSuccessModal } from '../SetDefaultTemplateSuccessModal/SetDefaultTemplateSuccessModal'
import { RemoveDefaultTemplateModal } from '../RemoveDefaultTemplateModal/RemoveDefaultTemplateModal'
import { RemoveDefaultTemplateSuccessModal } from '../RemoveDefaultTemplateSuccessModal/RemoveDefaultTemplateSuccessModal'
import { useArchiveTemplate } from '../hooks/useArchiveTemplate'
import { useSetDefaultTemplate } from '../hooks/useSetDefaultTemplate'
import { useRemoveDefaultTemplate } from '../hooks/useRemoveDefaultTemplate'
import { useTemplatesListData } from '../hooks/useTemplatesListData'

import {
  Badge,
  Button,
  FilterList,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip
} from '@percent/lemonade'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import { DefaultStar } from '@percent/partner-dashboard/common/components/DefaultStar/DefaultStar'
import { getFormattedDate } from '@percent/partner-dashboard/common/library/utility/date'
import { PBPListResponse } from '@percent/partner-dashboard/services/types/pbpResponses.types'
import {
  GetTemplatesListBaseProps,
  TemplateBase,
  TemplateStatus
} from '@percent/partner-dashboard/services/templates/templates.types'
import styles from './TemplatesList.module.scss'

export type TemplatesListProps<T extends TemplateBase> = {
  listFn: (params: GetTemplatesListBaseProps) => Promise<AxiosResponse<PBPListResponse<T>>>
  archiveTemplate: (templateId: string) => Promise<unknown>
  setDefaultTemplate: (templateId: string) => Promise<unknown>
  removeDefaultTemplate: (templateId: string) => Promise<unknown>
  nameTooltip?: (template: T) => string | undefined
  canManage: boolean
  searchPlaceholder: string
  createButtonText: string
  emptyTitle: string
  emptyDescription: string
  testIdPrefix: string
  createRoute: string
  editRoute: string
}

export function TemplatesList<T extends TemplateBase>({
  listFn,
  archiveTemplate,
  setDefaultTemplate,
  removeDefaultTemplate,
  nameTooltip,
  canManage,
  searchPlaceholder,
  createButtonText,
  emptyTitle,
  emptyDescription,
  testIdPrefix,
  createRoute,
  editRoute
}: Readonly<TemplatesListProps<T>>) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const openCreate = () => navigate(createRoute)

  const openEdit = (template: T) => navigate(editRoute.replace(':templateId', template.id))

  const {
    data,
    isLoading,
    errorMessage,
    totalResults,
    nextPage,
    previousPage,
    refresh,
    searchValue,
    searchQuery,
    handleSearchChange,
    handleSearchKeyDown,
    handleSearchClear,
    status,
    setStatus,
    sort,
    direction,
    handleRequestSort
  } = useTemplatesListData<T>(listFn)

  const { templateToArchive, setTemplateToArchive, isArchiving, handleArchive } = useArchiveTemplate<T>({
    archiveTemplate,
    onArchived: refresh
  })

  const {
    templateToSetDefault,
    setTemplateToSetDefault,
    isSettingDefault,
    handleSetDefault,
    setDefaultSuccess,
    closeSetDefaultSuccess
  } = useSetDefaultTemplate<T>({
    setDefaultTemplate,
    onSet: refresh
  })

  const {
    templateToRemoveDefault,
    setTemplateToRemoveDefault,
    isRemovingDefault,
    handleRemoveDefault,
    removeDefaultSuccess,
    closeRemoveDefaultSuccess
  } = useRemoveDefaultTemplate<T>({
    removeDefaultTemplate,
    onRemoved: refresh
  })

  const statusOptions = [
    { label: t('status.active'), value: TemplateStatus.Active },
    { label: t('status.archived'), value: TemplateStatus.Archived }
  ]

  const columns = useMemo(
    () => [
      { id: '', isSortable: false, props: { width: '2%' } },
      { id: 'name', content: t('table.header.templateName'), isSortable: false, props: { width: '26%' } },
      { id: 'status', content: t('table.header.status'), props: { width: '14%' } },
      { id: 'updatedAt', content: t('table.header.updatedAt'), isSortable: true, props: { width: '25%' } },
      { id: 'createdAt', content: t('table.header.createdAt'), isSortable: true, props: { width: '25%' } },
      { id: 'actions', content: ' ', isSortable: false, props: { width: '8%' } }
    ],
    [t]
  )

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={isLoading || !data}
        illustration="nothing-to-see"
        emptyTableTitle={searchQuery ? t('typography.noSearchTableListTitle', { searchTerm: searchQuery }) : emptyTitle}
        emptyTableDescription={searchQuery ? t('typography.noSearchTableListDescription') : emptyDescription}
        emptyTableButton={{
          label: createButtonText,
          onClick: openCreate
        }}
        sortDirection={direction}
        sortByColumn={sort ?? undefined}
        handleRequestSort={handleRequestSort}
      >
        <div className={styles.filterContainer}>
          <SearchInput
            name="search"
            value={searchValue}
            onKeyDown={handleSearchKeyDown}
            onChange={handleSearchChange}
            handleClearValue={handleSearchClear}
            placeholder={searchPlaceholder}
          />
          <FilterList
            label={t('table.status')}
            options={statusOptions}
            selectedValues={status}
            onChange={values => setStatus(values as TemplateStatus[])}
            testId={`${testIdPrefix}-status-filter`}
          />
          {canManage && (
            <Button onClick={openCreate} data-testid={`${testIdPrefix}-create-button`}>
              {createButtonText}
            </Button>
          )}
        </div>
        <Table>
          <TableBody>
            {data?.map(template => (
              <TableRow key={template.id} data-testid={`${testIdPrefix}-row-${template.id}`}>
                {template.isDefault ? (
                  <TableCell>
                    <DefaultStar />
                  </TableCell>
                ) : (
                  <TableCell>
                    <div />
                  </TableCell>
                )}
                <TableCell>
                  <Tooltip content={nameTooltip?.(template)}>
                    <span>{template.name}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Badge variant={template.status === TemplateStatus.Active ? 'positive' : 'default'}>
                    {t(`status.${template.status}`)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className={styles.updatedCell}>
                    <span>{getFormattedDate(template.updatedAt)}</span>
                    {template.updatedByProfile?.fullName && (
                      <span className={styles.updatedBy}>{template.updatedByProfile.fullName}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell>{getFormattedDate(template.createdAt)}</TableCell>
                <TableCell className={styles.actionsCell}>
                  <TemplateActionsMenu
                    canManage={canManage}
                    canEdit={template.status !== TemplateStatus.Archived}
                    canArchive={template.status !== TemplateStatus.Archived}
                    canSetDefault={template.status !== TemplateStatus.Archived && !template.isDefault}
                    canRemoveDefault={template.status !== TemplateStatus.Archived && template.isDefault}
                    onEdit={() => openEdit(template)}
                    onArchive={() => setTemplateToArchive(template)}
                    onSetDefault={() => setTemplateToSetDefault(template)}
                    onRemoveDefault={() => setTemplateToRemoveDefault(template)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults ?? 0 })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
      <ArchiveTemplateModal
        open={!!templateToArchive}
        handleClose={() => setTemplateToArchive(null)}
        handleArchive={handleArchive}
        isLoading={isArchiving}
      />
      <SetDefaultTemplateModal
        open={!!templateToSetDefault}
        handleClose={() => setTemplateToSetDefault(null)}
        handleSetDefault={handleSetDefault}
        isLoading={isSettingDefault}
      />
      <SetDefaultTemplateSuccessModal open={setDefaultSuccess} handleClose={closeSetDefaultSuccess} />
      <RemoveDefaultTemplateModal
        open={!!templateToRemoveDefault}
        handleClose={() => setTemplateToRemoveDefault(null)}
        handleRemoveDefault={handleRemoveDefault}
        isLoading={isRemovingDefault}
      />
      <RemoveDefaultTemplateSuccessModal open={removeDefaultSuccess} handleClose={closeRemoveDefaultSuccess} />
    </>
  )
}
